import { Injectable } from '@angular/core';
import { Observable, of, Subject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { RpcService } from '../shared/http/rpc.service';
import { ProductCategory } from './dmo/product-category';
import { ProductLayout } from './dmo/product-layout';

import { ProductConverterService } from './product-converter.service';

@Injectable()
export class ProductsService {
  public addCategoryMenu$: Observable<Array<ProductLayout>>;
  private layouts: Array<ProductLayout>;
  private categories: Array<ProductCategory>;
  private addCategoryMenuSource: Subject<Array<ProductLayout>> = new Subject<
    Array<ProductLayout>
  >();
  private readonly HIDDEN_LAYOUT_CATEGORY: string =
    '60848dcf-5452-4be8-97f7-97e224532a6e';

  constructor(
    private rpc: RpcService,
    private converter: ProductConverterService
  ) {
    this.layouts = null;
    this.categories = null;
    this.addCategoryMenu$ = this.addCategoryMenuSource.asObservable();
  }

  public getProductLayoutsAndCategories(): Observable<Array<ProductLayout>> {
    return combineLatest(
      this.getProductLayouts(),
      this.getProductCategories()
    ).pipe(map(this.converter.combineLayoutsAndCategories, this.converter));
  }

  public getProductLayouts(): Observable<Array<ProductLayout>> {
    if (this.layouts) {
      return of(this.layouts);
    } else {
      return this.rpc
        .makeManagerCall('getProductLayouts')
        .pipe(
          map((layouts: Array<ProductLayout>) =>
            layouts.map(this.converter.convertLayoutJsonToDmo, this.converter)
          )
        );
    }
  }

  public getProductLayout(id: number): Observable<ProductLayout> {
    return this.getProductLayouts().pipe(
      map((layouts: Array<ProductLayout>) => {
        return layouts.find((layout: ProductLayout) => layout.id === id);
      })
    );
  }

  public getProductCategories(): Observable<Array<ProductCategory>> {
    if (this.categories) {
      return of(this.categories);
    } else {
      return this.rpc
        .makeManagerCall('getProductCategories')
        .pipe(
          map((categories: [ProductCategory]) =>
            categories
              .filter(
                (category: ProductCategory) =>
                  category.name !== this.HIDDEN_LAYOUT_CATEGORY
              )
              .map(this.converter.convertCategoryJsonToDmo, this.converter)
          )
        );
    }
  }

  public getProductCategory(id: number): Observable<ProductCategory> {
    return this.getProductCategories().pipe(
      map((categories: Array<ProductCategory>) => {
        return categories.find(
          (category: ProductCategory) => category.id === id
        );
      })
    );
  }

  public addCategoryMenu(layouts: Array<ProductLayout>): void {
    this.addCategoryMenuSource.next(layouts);
  }
}
